import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ShareButton from '../components/ShareButton';
import { 
  ShoppingBag, Sparkles, BookOpen, Calendar, Lock, CreditCard, 
  Check, Star, Crown, Zap, ShoppingCart, Trash2, Tag, ArrowRight,
  X, Gift, Loader2, Package, Filter, Search, Globe, Wind, Heart,
  Play, FileText, ChevronDown
} from 'lucide-react';

// Currency configuration
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' }
];

export default function Shop() {
  const { token, user, refreshCart } = useAuth();
  const { currentColors } = useTheme();
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [premiumContent, setPremiumContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [premiumSearch, setPremiumSearch] = useState('');
  const [premiumFilter, setPremiumFilter] = useState('');
  const [cart, setCart] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'products', 'premium', 'subscriptions'
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  useEffect(() => {
    // Load saved currency preference
    const savedCurrency = localStorage.getItem('preferredCurrency') || 'USD';
    setSelectedCurrency(savedCurrency);
    loadShopData();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, productSearch]);

  useEffect(() => {
    loadPremiumContent();
  }, [premiumSearch, premiumFilter]);

  const loadShopData = async () => {
    try {
      const [plansRes, ratesRes, productsRes, categoriesRes, premiumRes] = await Promise.all([
        axios.get(`${API}/shop/plans`),
        axios.get(`${API}/shop/exchange-rates`),
        axios.get(`${API}/products?limit=50`),
        axios.get(`${API}/products/categories`),
        axios.get(`${API}/shop/premium-content`)
      ]);
      setPlans(plansRes.data);
      setExchangeRates(ratesRes.data);
      setProducts(productsRes.data.products || []);
      setCategories(categoriesRes.data.categories || []);
      setPremiumContent(premiumRes.data.content || []);
      
      if (token) {
        const cartRes = await axios.get(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart(cartRes.data);
      }
    } catch (error) {
      console.error('Failed to load shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (productSearch) params.append('search', productSearch);
      params.append('in_stock', 'true');
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadPremiumContent = async () => {
    try {
      const params = new URLSearchParams();
      if (premiumSearch) params.append('search', premiumSearch);
      if (premiumFilter) params.append('content_type', premiumFilter);
      
      const response = await axios.get(`${API}/shop/premium-content?${params}`);
      setPremiumContent(response.data.content || []);
    } catch (error) {
      console.error('Failed to load premium content:', error);
    }
  };

  const loadCart = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      if (refreshCart) refreshCart();
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };

  const changeCurrency = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    setShowCurrencyDropdown(false);
  };

  // Convert USD price to selected currency using exchange rates
  const convertPrice = (usdPrice) => {
    if (!usdPrice || usdPrice === 0) return 0;
    const rate = exchangeRates[selectedCurrency] || 1;
    return usdPrice * rate;
  };

  // Get price from multi-currency prices object, or convert from USD
  const getPrice = (prices, fallback = 0) => {
    if (!prices) return convertPrice(fallback);
    // If we have the exact currency price, use it
    if (prices[selectedCurrency]) return prices[selectedCurrency];
    // Otherwise convert from USD
    const usdPrice = prices['USD'] || fallback;
    return convertPrice(usdPrice);
  };

  // Get price for products (single USD price that needs conversion)
  const getProductPrice = (product) => {
    const basePrice = product.discount_percent > 0 
      ? product.price * (1 - product.discount_percent / 100) 
      : product.price;
    return convertPrice(basePrice);
  };

  // Get original price for products (before discount)
  const getProductOriginalPrice = (product) => {
    return convertPrice(product.price);
  };

  const formatPrice = (amount) => {
    const currency = CURRENCIES.find(c => c.code === selectedCurrency) || CURRENCIES[0];
    return `${currency.symbol}${(amount || 0).toFixed(2)}`;
  };

  const addToCart = async (contentType, contentId) => {
    if (!token) {
      alert('Please log in to add items to cart');
      return;
    }
    try {
      await axios.post(
        `${API}/cart/add`,
        { content_type: contentType, content_id: contentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadCart();
      setShowCart(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const removeFromCart = async (contentType, contentId) => {
    try {
      await axios.delete(
        `${API}/cart/remove/${contentType}/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadCart();
      setCouponResult(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove item');
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const response = await axios.post(
        `${API}/cart/apply-coupon?code=${couponCode}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCouponResult(response.data);
    } catch (error) {
      setCouponResult({ valid: false, error: error.response?.data?.detail || 'Invalid coupon' });
    }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/orders/checkout${couponResult?.valid ? `?coupon_code=${couponCode}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      await loadCart();
      setCouponCode('');
      setCouponResult(null);
      setShowCart(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckingOut(false);
    }
  };

  const getPlanIcon = (planId) => {
    const icons = { free: Sparkles, seeker: Star, enlightened: Crown };
    return icons[planId] || Zap;
  };

  const getContentIcon = (type) => {
    const icons = {
      meditation: Play,
      article: FileText,
      breathing: Wind,
      affirmation: Heart,
      session: Calendar
    };
    return icons[type] || Sparkles;
  };

  const cartTotal = couponResult?.valid ? couponResult.total : (cart?.subtotal || 0);
  const cartDiscount = couponResult?.valid ? couponResult.discount : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 rounded-full animate-spin"
             style={{ borderColor: `${currentColors?.primary}30`, borderTopColor: currentColors?.primary }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="shop-page">
      {/* Header */}
      <div className="glass-card p-8 relative overflow-visible" style={{ zIndex: 50 }}>
        <div className="absolute inset-0 sacred-geometry opacity-20 rounded-xl overflow-hidden"></div>
        <div className="relative z-10 overflow-visible">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 overflow-visible">
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${currentColors.primary}20` }}
                >
                  <ShoppingBag className="w-8 h-8" style={{ color: currentColors.primary }} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-cinzel" style={{ color: currentColors.primary }}>
                    Sacred Shop
                  </h1>
                  <p className="font-cormorant text-sm" style={{ color: currentColors.textMuted }}>
                    Unlock your spiritual potential
                  </p>
                </div>
              </div>
            </div>

            {/* Currency Selector */}
            <div className="relative" style={{ zIndex: 9999 }}>
              <button
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: `${currentColors.primary}15`,
                  border: `1px solid ${currentColors.primary}30`,
                  color: currentColors.text
                }}
                data-testid="currency-selector"
              >
                <Globe className="w-4 h-4" style={{ color: currentColors.primary }} />
                <span className="font-medium">{selectedCurrency}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showCurrencyDropdown && (
                <div 
                  className="fixed inset-0" 
                  style={{ zIndex: 99998 }}
                  onClick={() => setShowCurrencyDropdown(false)}
                />
              )}
              {showCurrencyDropdown && (
                <div 
                  className="absolute right-0 top-full mt-2 py-2 rounded-xl shadow-2xl min-w-[200px] max-h-[300px] overflow-y-auto"
                  style={{ 
                    zIndex: 99999,
                    backgroundColor: currentColors.cardBg || currentColors.background,
                    border: `1px solid ${currentColors.border}`,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                  }}
                >
                  {CURRENCIES.map(currency => (
                    <button
                      key={currency.code}
                      onClick={() => changeCurrency(currency.code)}
                      className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-opacity-50"
                      style={{ 
                        backgroundColor: selectedCurrency === currency.code ? `${currentColors.primary}15` : 'transparent',
                        color: currentColors.text
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-lg font-medium" style={{ color: currentColors.primary }}>
                          {currency.symbol}
                        </span>
                        <span>{currency.name}</span>
                      </span>
                      {selectedCurrency === currency.code && (
                        <Check className="w-4 h-4" style={{ color: currentColors.primary }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cart Button */}
      {token && cart && cart.items?.length > 0 && (
        <button
          onClick={() => setShowCart(!showCart)}
          className="fixed bottom-20 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all hover:scale-105"
          style={{ 
            backgroundColor: currentColors.primary,
            color: '#fff'
          }}
          data-testid="cart-button"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-medium">{cart.items.length} items</span>
          <span className="px-2 py-0.5 rounded-full text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            {formatPrice(cart.subtotal || 0)}
          </span>
        </button>
      )}

      {/* Cart Sidebar */}
      {showCart && cart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCart(false)}
          />
          <div 
            className="relative w-full max-w-md h-full overflow-y-auto p-6"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-cinzel" style={{ color: currentColors.primary }}>
                Your Cart
              </h2>
              <div className="flex items-center gap-2">
                {/* Cart Currency Display */}
                <span 
                  className="px-2 py-1 rounded-lg text-xs"
                  style={{ backgroundColor: `${currentColors.primary}15`, color: currentColors.primary }}
                >
                  {selectedCurrency}
                </span>
                <button 
                  onClick={() => setShowCart(false)}
                  className="p-2 rounded-lg hover:opacity-80"
                  style={{ color: currentColors.textMuted }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {cart.items?.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}30` }} />
                <p style={{ color: currentColors.textMuted }}>Your cart is empty</p>
              </div>
            ) : (
              <>
                {/* Cart Items */}
                <div className="space-y-4 mb-6">
                  {cart.items.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center gap-4 p-4 rounded-xl"
                      style={{ backgroundColor: `${currentColors.primary}10` }}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: currentColors.text }}>
                          {item.title}
                        </p>
                        <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                          {item.content_type}
                        </p>
                      </div>
                      <p className="font-semibold" style={{ color: currentColors.primary }}>
                        {formatPrice(item.price)}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.content_type, item.content_id)}
                        className="p-2 rounded-lg hover:opacity-80"
                        style={{ color: currentColors.textMuted }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Coupon */}
                <div className="mb-6">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Coupon code"
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{ 
                        backgroundColor: `${currentColors.primary}10`,
                        border: `1px solid ${currentColors.border}`,
                        color: currentColors.text
                      }}
                    />
                    <button
                      onClick={applyCoupon}
                      className="px-4 py-2 rounded-lg font-medium text-sm"
                      style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                    >
                      Apply
                    </button>
                  </div>
                  {couponResult && (
                    <p 
                      className="text-xs mt-2"
                      style={{ color: couponResult.valid ? '#22c55e' : '#ef4444' }}
                    >
                      {couponResult.valid ? `Discount: -${formatPrice(cartDiscount)}` : couponResult.error}
                    </p>
                  )}
                </div>

                {/* Totals */}
                <div 
                  className="border-t pt-4 space-y-2"
                  style={{ borderColor: currentColors.border }}
                >
                  <div className="flex justify-between">
                    <span style={{ color: currentColors.textMuted }}>Subtotal</span>
                    <span style={{ color: currentColors.text }}>{formatPrice(cart.subtotal || 0)}</span>
                  </div>
                  {cartDiscount > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#22c55e' }}>Discount</span>
                      <span style={{ color: '#22c55e' }}>-{formatPrice(cartDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-semibold pt-2">
                    <span style={{ color: currentColors.text }}>Total</span>
                    <span style={{ color: currentColors.primary }}>{formatPrice(cartTotal)}</span>
                  </div>
                </div>

                {/* Checkout */}
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full mt-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Checkout
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabs - Order: All, Physical Products, Premium Content, Subscriptions */}
      <div className="flex flex-wrap justify-center gap-2">
        {[
          { id: 'all', label: 'All Items', icon: ShoppingBag },
          { id: 'products', label: 'Physical Products', icon: Package },
          { id: 'premium', label: 'Premium Content', icon: Crown },
          { id: 'subscriptions', label: 'Subscriptions', icon: Star }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all"
            style={{ 
              backgroundColor: activeTab === tab.id ? currentColors.primary : `${currentColors.primary}15`,
              color: activeTab === tab.id ? '#fff' : currentColors.text,
              border: `1px solid ${activeTab === tab.id ? currentColors.primary : currentColors.border}`
            }}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* All Items Tab - Shows everything */}
      {activeTab === 'all' && (
        <div className="space-y-8">
          {/* Physical Products Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.primary }}>
                <Package className="w-5 h-5" />
                Physical Products
              </h2>
              <button
                onClick={() => setActiveTab('products')}
                className="text-sm font-medium hover:underline"
                style={{ color: currentColors.accent }}
              >
                View All →
              </button>
            </div>
            
            {products.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <Package className="w-12 h-12 mx-auto mb-3" style={{ color: `${currentColors.primary}30` }} />
                <p style={{ color: currentColors.textMuted }}>No products available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {products.slice(0, 4).map((product) => (
                  <div 
                    key={product.id}
                    className="glass-card p-0 overflow-hidden group hover:shadow-xl transition-all"
                  >
                    <div className="relative h-36 overflow-hidden">
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0].startsWith('http') ? product.images[0] : `${API}${product.images[0]}`}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ backgroundColor: `${currentColors.primary}10` }}
                        >
                          <Package className="w-10 h-10" style={{ color: `${currentColors.primary}40` }} />
                        </div>
                      )}
                      {product.discount_percent > 0 && (
                        <div 
                          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ backgroundColor: '#ef4444', color: '#fff' }}
                        >
                          -{product.discount_percent}%
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs mb-1" style={{ color: currentColors.textMuted }}>{product.category}</p>
                      <h3 className="font-medium text-sm mb-2 line-clamp-1" style={{ color: currentColors.text }}>{product.name}</h3>
                      <div className="flex items-center justify-between">
                        <span className="font-bold" style={{ color: currentColors.primary }}>
                          {formatPrice(getProductPrice(product))}
                        </span>
                        <button
                          onClick={() => addToCart('product', product.id)}
                          className="p-1.5 rounded-lg transition-all hover:opacity-80"
                          style={{ backgroundColor: currentColors.primary }}
                        >
                          <ShoppingCart className="w-3.5 h-3.5 text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Premium Content Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.primary }}>
                <Crown className="w-5 h-5" />
                Premium Content
              </h2>
              <button
                onClick={() => setActiveTab('premium')}
                className="text-sm font-medium hover:underline"
                style={{ color: currentColors.accent }}
              >
                View All →
              </button>
            </div>
            
            {premiumContent.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <Crown className="w-12 h-12 mx-auto mb-3" style={{ color: `${currentColors.primary}30` }} />
                <p style={{ color: currentColors.textMuted }}>No premium content available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {premiumContent.slice(0, 4).map((item) => {
                  const Icon = getContentIcon(item.content_type);
                  const itemPrice = getPrice(item.prices, 9.99);
                  return (
                    <div 
                      key={`${item.content_type}-${item.id}`}
                      className="glass-card p-0 overflow-hidden group hover:shadow-xl transition-all"
                    >
                      <div className="relative h-36 overflow-hidden">
                        {item.image_url ? (
                          <img 
                            src={item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ background: `linear-gradient(135deg, ${currentColors.primary}30, ${currentColors.secondary}30)` }}
                          >
                            <Icon className="w-10 h-10" style={{ color: currentColors.primary }} />
                          </div>
                        )}
                        <div 
                          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                        >
                          {item.type_label}
                        </div>
                        <div 
                          className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000' }}
                        >
                          {formatPrice(itemPrice)}
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="font-medium text-sm mb-2 line-clamp-1" style={{ color: currentColors.text }}>{item.title}</h3>
                        <div className="flex items-center justify-between">
                          <span className="font-bold" style={{ color: currentColors.primary }}>{formatPrice(itemPrice)}</span>
                          <button
                            onClick={() => addToCart(item.content_type, item.id)}
                            className="p-1.5 rounded-lg transition-all hover:opacity-80"
                            style={{ backgroundColor: currentColors.primary }}
                          >
                            <ShoppingCart className="w-3.5 h-3.5 text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Subscription Plans Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.primary }}>
                <Star className="w-5 h-5" />
                Subscription Plans
              </h2>
              <button
                onClick={() => setActiveTab('subscriptions')}
                className="text-sm font-medium hover:underline"
                style={{ color: currentColors.accent }}
              >
                View All →
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const Icon = getPlanIcon(plan.id);
                const isPopular = plan.id === 'seeker';
                return (
                  <div 
                    key={plan.id}
                    className={`glass-card p-4 relative overflow-hidden ${isPopular ? 'ring-2' : ''}`}
                    style={{ 
                      borderColor: isPopular ? currentColors.primary : undefined,
                      ringColor: isPopular ? currentColors.primary : undefined
                    }}
                  >
                    {isPopular && (
                      <div 
                        className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                      >
                        Popular
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${currentColors.primary}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: currentColors.primary }} />
                      </div>
                      <div>
                        <h3 className="font-semibold" style={{ color: currentColors.text }}>{plan.name}</h3>
                        <div>
                          <span className="text-xl font-bold" style={{ color: currentColors.primary }}>
                            {formatPrice(getPrice(plan.prices, plan.price))}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-xs ml-1" style={{ color: currentColors.textMuted }}>/mo</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <ul className="space-y-1.5 mb-3">
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-xs">
                          <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                          <span style={{ color: currentColors.textMuted }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button
                      onClick={() => plan.price > 0 && addToCart('subscription', plan.id)}
                      disabled={plan.price === 0}
                      className="w-full py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50"
                      style={{ 
                        backgroundColor: plan.price > 0 ? currentColors.primary : `${currentColors.primary}20`,
                        color: plan.price > 0 ? '#fff' : currentColors.text
                      }}
                    >
                      {plan.price === 0 ? 'Current Plan' : 'Subscribe'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Premium Content Tab */}
      {activeTab === 'premium' && (
        <div className="space-y-6">
          <div className="glass-card p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div>
                <h2 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.primary }}>
                  <Crown className="w-5 h-5" />
                  Premium Content
                </h2>
                <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
                  One-time purchase for lifetime access
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* Filter by type */}
                <select
                  value={premiumFilter}
                  onChange={(e) => setPremiumFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ 
                    backgroundColor: `${currentColors.primary}10`,
                    border: `1px solid ${currentColors.border}`,
                    color: currentColors.text
                  }}
                >
                  <option value="">All Types</option>
                  <option value="session">Healing Sessions</option>
                  <option value="meditation">Meditations</option>
                  <option value="article">Articles</option>
                  <option value="breathing">Breathing</option>
                  <option value="affirmation">Affirmations</option>
                </select>
                
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                  <input
                    type="text"
                    value={premiumSearch}
                    onChange={(e) => setPremiumSearch(e.target.value)}
                    placeholder="Search..."
                    className="pl-9 pr-4 py-2 rounded-lg text-sm w-48"
                    style={{ 
                      backgroundColor: `${currentColors.primary}10`,
                      border: `1px solid ${currentColors.border}`,
                      color: currentColors.text
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {premiumContent.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Crown className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}30` }} />
              <p style={{ color: currentColors.textMuted }}>No premium content available yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {premiumContent.map((item) => {
                const Icon = getContentIcon(item.content_type);
                const itemPrice = getPrice(item.prices, 9.99);
                return (
                  <div 
                    key={`${item.content_type}-${item.id}`}
                    className="glass-card p-0 overflow-hidden group hover:shadow-xl transition-all"
                    data-testid={`premium-item-${item.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-40 overflow-hidden">
                      {item.image_url ? (
                        <img 
                          src={item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${currentColors.primary}30, ${currentColors.secondary}30)` }}
                        >
                          <Icon className="w-12 h-12" style={{ color: currentColors.primary }} />
                        </div>
                      )}
                      {/* Type badge */}
                      <div 
                        className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm"
                        style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                      >
                        {item.type_label}
                      </div>
                      {/* Price badge - High contrast */}
                      <div 
                        className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-bold shadow-lg"
                        style={{ 
                          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                          color: '#000',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                        }}
                      >
                        {formatPrice(itemPrice)}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-sm line-clamp-2 flex-1" style={{ color: currentColors.text }}>
                          {item.title}
                        </h3>
                        <ShareButton
                          title={item.title}
                          description={item.description}
                          imageUrl={item.image_url?.startsWith('http') ? item.image_url : `${API}${item.image_url}`}
                          contentType={item.content_type}
                          contentId={item.id}
                          variant="icon"
                        />
                      </div>
                      <p className="text-xs line-clamp-2 mb-3" style={{ color: currentColors.textMuted }}>
                        {item.description}
                      </p>
                      
                      {/* Price display in content area for better visibility */}
                      <div 
                        className="flex items-center justify-between mb-3 py-2 px-3 rounded-lg"
                        style={{ backgroundColor: `${currentColors.primary}10` }}
                      >
                        <span className="text-xs font-medium" style={{ color: currentColors.textMuted }}>
                          {item.duration ? `${item.duration} min` : item.instructor ? `with ${item.instructor}` : 'One-time purchase'}
                        </span>
                        <span 
                          className="text-lg font-bold"
                          style={{ color: currentColors.primary }}
                          data-testid={`price-${item.id}`}
                        >
                          {formatPrice(itemPrice)}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => addToCart(item.content_type, item.id)}
                        className="w-full py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 hover:opacity-90"
                        style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                        data-testid={`add-to-cart-${item.id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add to Cart
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="glass-card p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <h2 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.primary }}>
                <Package className="w-5 h-5" />
                Physical Products
              </h2>
              
              <div className="flex flex-wrap gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ 
                    backgroundColor: `${currentColors.primary}10`,
                    border: `1px solid ${currentColors.border}`,
                    color: currentColors.text
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products..."
                    className="pl-9 pr-4 py-2 rounded-lg text-sm w-48"
                    style={{ 
                      backgroundColor: `${currentColors.primary}10`,
                      border: `1px solid ${currentColors.border}`,
                      color: currentColors.text
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}30` }} />
              <p style={{ color: currentColors.textMuted }}>No products available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <div 
                  key={product.id}
                  className="glass-card p-0 overflow-hidden group hover:shadow-xl transition-all"
                >
                  <div className="relative h-48 overflow-hidden">
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0].startsWith('http') ? product.images[0] : `${API}${product.images[0]}`}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: `${currentColors.primary}10` }}
                      >
                        <Package className="w-12 h-12" style={{ color: `${currentColors.primary}40` }} />
                      </div>
                    )}
                    {product.discount_percent > 0 && (
                      <div 
                        className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-bold"
                        style={{ backgroundColor: '#ef4444', color: '#fff' }}
                      >
                        -{product.discount_percent}%
                      </div>
                    )}
                    {/* Share button on hover */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ShareButton
                        title={product.name}
                        description={product.description}
                        imageUrl={product.images?.[0]?.startsWith('http') ? product.images[0] : `${API}${product.images?.[0]}`}
                        contentType="product"
                        contentId={product.id}
                        variant="card"
                        className="!p-2 !bg-white/90 backdrop-blur-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <p className="text-xs mb-1" style={{ color: currentColors.textMuted }}>
                      {product.category}
                    </p>
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2" style={{ color: currentColors.text }}>
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        {product.discount_percent > 0 ? (
                          <>
                            <span className="font-bold" style={{ color: currentColors.primary }}>
                              {formatPrice(getProductPrice(product))}
                            </span>
                            <span className="text-xs line-through ml-2" style={{ color: currentColors.textMuted }}>
                              {formatPrice(getProductOriginalPrice(product))}
                            </span>
                          </>
                        ) : (
                          <span className="font-bold" style={{ color: currentColors.primary }}>
                            {formatPrice(getProductPrice(product))}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => addToCart('product', product.id)}
                        className="p-2 rounded-lg transition-all hover:opacity-80"
                        style={{ backgroundColor: currentColors.primary }}
                      >
                        <ShoppingCart className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-6">
          <div className="glass-card p-6 text-center">
            <h2 className="text-xl font-cinzel flex items-center justify-center gap-2" style={{ color: currentColors.primary }}>
              <Star className="w-5 h-5" />
              Subscription Plans
            </h2>
            <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>
              Get unlimited access to all premium content
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const Icon = getPlanIcon(plan.id);
              const isPopular = plan.id === 'seeker';
              return (
                <div 
                  key={plan.id}
                  className={`glass-card p-6 relative overflow-hidden ${isPopular ? 'ring-2' : ''}`}
                  style={{ 
                    borderColor: isPopular ? currentColors.primary : undefined,
                    ringColor: isPopular ? currentColors.primary : undefined
                  }}
                >
                  {isPopular && (
                    <div 
                      className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                    >
                      Popular
                    </div>
                  )}
                  
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${currentColors.primary}20` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: currentColors.primary }} />
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-2" style={{ color: currentColors.text }}>
                    {plan.name}
                  </h3>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold" style={{ color: currentColors.primary }}>
                      {formatPrice(getPrice(plan.prices, plan.price))}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-sm ml-1" style={{ color: currentColors.textMuted }}>/month</span>
                    )}
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                        <span style={{ color: currentColors.textMuted }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => plan.price > 0 && addToCart('subscription', plan.id)}
                    disabled={plan.price === 0}
                    className="w-full py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                    style={{ 
                      backgroundColor: plan.price > 0 ? currentColors.primary : `${currentColors.primary}20`,
                      color: plan.price > 0 ? '#fff' : currentColors.text
                    }}
                  >
                    {plan.price === 0 ? 'Current Plan' : 'Subscribe'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
